import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { validateDevToken } from 'zite-endpoints-sdk';

type DevAuthContextType = {
  isDevMode: boolean;
  isValidating: boolean;
  devUser: DevUser | null;
};

type DevUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  active: boolean;
};

const DEV_USER: DevUser = {
  id: 'dev-codex-session',
  email: 'dev@codex.local',
  firstName: 'Codex',
  lastName: 'Dev',
  role: 'Owner',
  active: true,
};

const SESSION_KEY = '__dev_token_validated';

function getInitialState() {
  // Synchronous check — must run during useState init, NOT in useEffect
  if (typeof window === 'undefined') return { isDevMode: false, isValidating: false, tokenToValidate: null as string | null };

  if (sessionStorage.getItem(SESSION_KEY) === 'true') {
    return { isDevMode: true, isValidating: false, tokenToValidate: null };
  }

  const params = new URLSearchParams(window.location.search);
  const token = params.get('dev_token');
  if (token) {
    return { isDevMode: false, isValidating: true, tokenToValidate: token };
  }

  return { isDevMode: false, isValidating: false, tokenToValidate: null };
}

const DevAuthContext = createContext<DevAuthContextType>({
  isDevMode: false,
  isValidating: false,
  devUser: null,
});

export function useDevAuth() {
  return useContext(DevAuthContext);
}

export default function DevAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(getInitialState);

  useEffect(() => {
    if (!state.tokenToValidate) return;

    validateDevToken({ token: state.tokenToValidate })
      .then((res) => {
        if (res.valid) {
          sessionStorage.setItem(SESSION_KEY, 'true');
          // Strip token from URL
          const url = new URL(window.location.href);
          url.searchParams.delete('dev_token');
          window.history.replaceState({}, '', url.toString());
          setState({ isDevMode: true, isValidating: false, tokenToValidate: null });
        } else {
          setState({ isDevMode: false, isValidating: false, tokenToValidate: null });
        }
      })
      .catch(() => {
        setState({ isDevMode: false, isValidating: false, tokenToValidate: null });
      });
  }, [state.tokenToValidate]);

  const isDevMode = state.isDevMode;

  return (
    <DevAuthContext.Provider value={{ isDevMode, isValidating: state.isValidating, devUser: isDevMode ? DEV_USER : null }}>
      {isDevMode && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-yellow-500 text-black text-center text-xs font-bold py-1 tracking-wide">
          ⚡ DEV MODE — Codex/Playwright session
        </div>
      )}
      {children}
    </DevAuthContext.Provider>
  );
}
